import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import earthImg from 'assets/brand/earth@2x.png';
import zodLocales from 'common/zod/locales';
import { zipCodeSchema } from 'common/zod/schemas';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import IconButton from 'components/_core/IconButton';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import Loader from 'components/Loader';
import useToast from 'components/ToastProvider/useToast';
import AddressSearchInput from 'features/Onboarding/components/AddressSearchInput';
import * as Layout from 'layouts/StepperLayout';

import SelectOnlyFrance from '../../../Onboarding/components/SelectOnlyFrance';
import * as routes from '../../routes';
import locales from './locales';
import { UpdateUserAddressDocument } from './mutations/updateUserAddress.gql';
import { AddressChangeQueryDocument } from './queries/getUserAddress.gql';

const MAX_STREET_CHARACTERS_LENGTH = 45;

const addressSchema = z.object({
  city: z.string().min(1, zodLocales.errors.required),
  country: z.string().min(1, zodLocales.errors.required),
  street: z
    .string()
    .min(1, zodLocales.errors.required)
    .max(
      MAX_STREET_CHARACTERS_LENGTH,
      locales.formatString(
        locales.inputs.street.errors.limitedLength,
        MAX_STREET_CHARACTERS_LENGTH,
      ) as string,
    ),
  streetAddition: z.string().optional().default(''),
  zip: zipCodeSchema,
});

type AddressPayload = z.infer<typeof addressSchema>;

const EditAddress: FC = () => {
  const history = useHistory();
  const { showToast } = useToast();

  const addressOverlay = useDisclosure();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isManualMode, setIsManualMode] = useState(false);

  const { data, loading: loadingQuery } = useQuery(AddressChangeQueryDocument);
  const [updateUserProfile, { loading: loadingMutation }] = useMutation(
    UpdateUserAddressDocument,
  );

  const initialValues = {
    city: data?.viewer?.profile.city ?? undefined,
    country: data?.viewer?.profile.country ?? undefined,
    street: data?.viewer?.profile.street ?? undefined,
    streetAddition: data?.viewer?.profile.streetAddition ?? undefined,
    zip: data?.viewer?.profile.zip ?? undefined,
  };

  const [hasSelectedAddress, setHasSelectedAddress] = useState(false);
  const [hasAddressComplement, setHasAddressComplement] = useState(
    Boolean(initialValues.streetAddition),
  );

  const isVerifying =
    initialValues.street || isManualMode || hasSelectedAddress;

  const {
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    register,
    reset,
    setFocus,
    setValue,
    trigger: validateForm,
  } = useForm<AddressPayload>({
    defaultValues: initialValues,
    mode: initialValues.street ? 'onChange' : 'onSubmit',
    resolver: zodResolver(addressSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    await updateUserProfile({
      variables: {
        input: {
          city: values.city,
          country: 'FR',
          street: values.street,
          streetAddition: values.streetAddition,
          zip: values.zip,
        },
      },
    });

    showToast({
      message: locales.success,
      type: 'success',
    });

    history.push(routes.root);
  });

  useEffect(() => {
    if (
      data?.viewer?.profile.street ||
      data?.viewer?.profile.city ||
      data?.viewer?.profile.zip
    ) {
      validateForm();
    }
  }, [data?.viewer?.profile, validateForm]);

  useLayoutEffect(() => {
    // Focus the streetAddition field when you just clicked on the add button
    // We can't do the focus in the callback of the button because the input is not displayed yet
    if (hasAddressComplement && !getValues('streetAddition')) {
      setFocus('streetAddition');
    }
  }, [hasAddressComplement, setFocus, getValues]);

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Stack paddingBottom="space-40" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text>{locales.subtitle}</Text>
          </Stack>

          {loadingQuery ? (
            <Loader />
          ) : (
            <Box ref={containerRef}>
              <Flex
                direction="column"
                display={addressOverlay.isOpen ? 'none' : undefined}
              >
                <Flex
                  as="form"
                  direction="column"
                  height="full"
                  justify="space-between"
                  noValidate
                  onSubmit={onSubmit}
                >
                  <VStack
                    alignItems="stretch"
                    paddingBottom="space-40"
                    spacing="space-16"
                  >
                    <Form.Field
                      error={errors.street?.message}
                      label={locales.inputs.street.label}
                    >
                      {isManualMode ? (
                        <Form.Input {...register('street')} required />
                      ) : (
                        <AddressSearchInput
                          {...register('street')}
                          allowManualMode
                          isDesktop
                          isOverlayOpen={addressOverlay.isOpen}
                          isRequired
                          label={locales.inputs.street.label}
                          onOverlayClose={addressOverlay.onClose}
                          onOverlayOpen={addressOverlay.onOpen}
                          onSelectAddress={({
                            city,
                            street,
                            streetAddition,
                            zip,
                          }) => {
                            reset();
                            setValue('street', street);
                            setValue('city', city);
                            setValue('zip', zip);
                            setValue('streetAddition', streetAddition ?? '');
                            setHasAddressComplement(Boolean(streetAddition));
                            setHasSelectedAddress(true);
                          }}
                          onSelectedManualMode={() => {
                            reset();
                            setIsManualMode(true);
                            setValue('street', '');
                            setValue('streetAddition', '');
                            setValue('city', '');
                            setValue('zip', '');
                          }}
                          overlayContainerRef={containerRef}
                          placeholder={
                            isVerifying
                              ? undefined
                              : locales.inputs.street.placeholder
                          }
                          searchPlaceholder={locales.inputs.street.placeholder}
                        />
                      )}
                    </Form.Field>

                    {isVerifying ? (
                      <>
                        <Form.Field
                          display={hasAddressComplement ? undefined : 'none'}
                          error={errors.streetAddition?.message}
                          label={locales.inputs.streetAddition.label}
                        >
                          <Flex alignItems="center" gap="space-8">
                            <Form.Input
                              {...register('streetAddition')}
                              placeholder={
                                locales.inputs.streetAddition.placeholder
                              }
                            />
                            <IconButton
                              aria-label={locales.clear}
                              icon="trash"
                              onClick={() => {
                                setValue('streetAddition', '');
                                setHasAddressComplement(false);
                              }}
                              variant="inline-secondary"
                            />
                          </Flex>
                        </Form.Field>
                        <Button
                          display={hasAddressComplement ? 'none' : undefined}
                          onClick={() => {
                            setHasAddressComplement(true);
                          }}
                          variant="inline-secondary"
                          width="fit-content"
                        >
                          {locales.addStreetAddition}
                        </Button>
                        <SimpleGrid columns={2} spacing="space-16">
                          <Form.Field
                            error={errors.zip?.message}
                            label={locales.inputs.zip.label}
                          >
                            <Form.Input
                              {...register('zip')}
                              inputMode="numeric"
                              maxLength={5}
                              required
                            />
                          </Form.Field>

                          <Form.Field
                            error={errors.city?.message}
                            label={locales.inputs.city.label}
                          >
                            <Form.Input {...register('city')} required />
                          </Form.Field>
                        </SimpleGrid>
                        <SelectOnlyFrance />
                      </>
                    ) : null}
                  </VStack>
                  {isVerifying ? (
                    <Box>
                      <Button
                        icon="tick"
                        isDisabled={loadingQuery || loadingMutation || !isValid}
                        isLoading={loadingMutation}
                        type="submit"
                      >
                        {locales.ctaPrimary}
                      </Button>
                    </Box>
                  ) : null}
                </Flex>
              </Flex>
            </Box>
          )}
        </Layout.BodyScrollableSection>
      </Layout.Body>

      <Layout.Sidebar>
        <Image role="presentation" src={earthImg} />
      </Layout.Sidebar>
    </Layout.Root>
  );
};

export default EditAddress;
